import React, { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { BottomNav } from "@/components/Nav";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Upload } from "lucide-react";

function today() { return new Date().toISOString().slice(0,10); }

export default function Progress() {
  const [weights, setWeights] = useState([]);
  const [form, setForm] = useState({ date: today(), weight_kg: "", waist_cm: "" });
  const [photos, setPhotos] = useState([]);
  const [err, setErr] = useState("");
  const [photoUrls, setPhotoUrls] = useState({});

  const load = async () => {
    const [w, p] = await Promise.all([api.get("/progress/weight"), api.get("/progress/photos")]);
    setWeights(w.data); setPhotos(p.data);
    // Load photo blobs
    const urls = {};
    for (const ph of p.data) {
      try {
        const token = localStorage.getItem("kw_token");
        const resp = await fetch(`${API_BASE}/progress/photo/${ph.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
        if (resp.ok) {
          const blob = await resp.blob();
          urls[ph.id] = URL.createObjectURL(blob);
        }
      } catch {}
    }
    setPhotoUrls(urls);
  };
  useEffect(() => { load(); return () => Object.values(photoUrls).forEach(URL.revokeObjectURL); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/progress/weight", {
        date: form.date,
        weight_kg: parseFloat(form.weight_kg),
        waist_cm: form.waist_cm ? parseFloat(form.waist_cm) : null,
      });
      setForm({ date: today(), weight_kg: "", waist_cm: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("kw_token");
      const resp = await fetch(`${API_BASE}/progress/photo`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error((await resp.json()).detail || "Upload failed");
      load();
    } catch (e) { setErr(String(e.message||e)); }
  };

  const chartData = weights.map(w => ({ date: w.date.slice(5), weight: w.weight_kg }));
  const first = weights[0], last = weights[weights.length-1];
  const delta = first && last ? (last.weight_kg - first.weight_kg) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white safe-pb">
      <div className="max-w-xl mx-auto px-4 pt-6">
        <h1 className="font-display font-black uppercase text-3xl tracking-tighter">Progress</h1>

        <div className="kw-card p-4 mt-4">
          <div className="flex justify-between items-end">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Weight change</div>
              <div className="font-display font-black text-4xl">{delta>0?"+":""}{delta.toFixed(1)} kg</div>
              <div className="text-xs text-neutral-500">From {first?.weight_kg||"-"} to {last?.weight_kg||"-"} kg</div>
            </div>
          </div>
          <div className="h-48 mt-4" data-testid="weight-chart">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#262626" strokeDasharray="2 3"/>
                <XAxis dataKey="date" stroke="#737373" fontSize={10}/>
                <YAxis stroke="#737373" fontSize={10} domain={["dataMin - 2","dataMax + 2"]}/>
                <Tooltip contentStyle={{background:"#171717",border:"1px solid #333",fontSize:12}}/>
                <Line type="monotone" dataKey="weight" stroke="#D4AF37" strokeWidth={2} dot={{fill:"#D4AF37",r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <form onSubmit={save} className="kw-card p-4 mt-5">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Log weight / waist</div>
          <div className="grid grid-cols-3 gap-2">
            <input className="kw-input" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} data-testid="wlog-date"/>
            <input className="kw-input" type="number" step="0.1" placeholder="kg" value={form.weight_kg} onChange={e=>setForm({...form,weight_kg:e.target.value})} required data-testid="wlog-kg"/>
            <input className="kw-input" type="number" step="0.1" placeholder="waist cm" value={form.waist_cm} onChange={e=>setForm({...form,waist_cm:e.target.value})} data-testid="wlog-waist"/>
          </div>
          {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
          <button className="kw-btn-primary w-full mt-3" data-testid="wlog-submit">Save</button>
        </form>

        <div className="kw-card p-4 mt-5">
          <div className="flex justify-between items-center">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Progress photos</div>
            <label className="kw-btn-outline text-xs flex items-center gap-1 cursor-pointer" data-testid="photo-upload-label">
              <Upload className="w-3 h-3"/>Upload
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input"/>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {photos.map(p => (
              <div key={p.id} className="aspect-[3/4] bg-[#171717] border border-[#262626] overflow-hidden" data-testid={`photo-${p.id}`}>
                {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt="progress" className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-xs text-neutral-500">Loading…</div>}
              </div>
            ))}
            {photos.length===0 && <div className="col-span-3 text-sm text-neutral-500 text-center py-6">No photos yet.</div>}
          </div>
          <p className="text-[11px] text-neutral-500 mt-3">Photos are stored privately. Only you and your coach can see them.</p>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
